"use client";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Check, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function PourLesProfessionnels() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [isHovering, setIsHovering] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!buttonRef.current || !isHovering) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      setMousePosition({
        x,
        y
      });
    };
    if (isHovering) {
      window.addEventListener("mousemove", handleMouseMove);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isHovering]);
  const benefits = [{
    id: 1,
    title: "Accès à une large clientèle",
    description: "Développez votre activité grâce à notre réseau de particuliers"
  }, {
    id: 2,
    title: "Gestion simplifiée",
    description: "Outils professionnels pour gérer vos devis et interventions"
  }, {
    id: 3,
    title: "Visibilité optimale",
    description: "Mettez en avant votre savoir-faire et vos réalisations"
  }, {
    id: 4,
    title: "Paiement sécurisé",
    description: "Transactions protégées et versements rapides"
  }];
  const features = ["Inscription gratuite et validation de votre profil sous 48h", "Système de notation et avis clients pour renforcer votre crédibilité", "Formation continue et accompagnement personnalisé", "Outils de gestion intégrés : devis, facturation, agenda"];
  return <section className="relative min-h-screen bg-dark-background">
      <div className="grid lg:grid-cols-2">
        {}
        <div className="relative h-screen lg:sticky lg:top-0 overflow-hidden">
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1442601/img/craftsman_workshop_professional.png)] bg-cover bg-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark-background/40" />
        </div>

        {}
        <div ref={textRef} className="relative bg-background text-foreground px-6 py-16 lg:px-12 lg:py-24">
          <div className={`max-w-2xl mx-auto space-y-12 transition-all duration-[var(--duration-slow)] ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20">
                <Users className="size-5 text-secondary-dark" />
                <span className="text-sm font-semibold text-secondary-dark">
                  Espace Professionnel
                </span>
              </div>

              <h1 className="text-4xl lg:text-5xl font-display font-bold leading-tight">
                Rejoignez le réseau{" "}
                <span className="text-primary-dark">Be Travaux</span>
              </h1>

              <p className="text-lg text-foreground leading-relaxed">
                Développez votre activité en rejoignant notre plateforme de mise
                en relation entre particuliers et artisans qualifiés. Accédez à
                de nouvelles opportunités et gérez vos projets en toute
                simplicité.
              </p>
            </div>

            {}
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="bg-secondary/10 border border-secondary/20 rounded-lg p-6 space-y-3 transition-all duration-[var(--duration-medium)]">
                  <div className={`flex items-center justify-center size-12 rounded-full ${i === 0 ? "bg-secondary/20" : i === 1 ? "bg-secondary/20" : "bg-primary/20"}`}>
                    <Check className={`size-6 ${i === 0 ? "text-secondary-dark" : i === 1 ? "text-secondary-dark" : "text-primary-dark"}`} />
                  </div>
                  <h3 className="text-xl font-display font-semibold">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </div>)}
            </div>

            {}
            <div className="space-y-6 pt-8">
              <h2 className="text-2xl font-display font-bold">
                Pourquoi choisir Be Travaux ?
              </h2>

              <ul className="space-y-4">
                {features.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-foreground">
                    <div className="flex items-center justify-center size-6 rounded-full bg-secondary/20 shrink-0 mt-0.5">
                      <Check className="size-4 text-secondary-dark" />
                    </div>
                    <span className="leading-relaxed">{item}</span>
                  </li>)}
              </ul>
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-4 pt-8">
              <div className="flex-1">
                <Link to="/inscription-pro/">
                  <Button size="lg" variant="secondary" className="w-full shadow-soft">
                    Créer mon compte Pro
                  </Button>
                </Link>
              </div>

              {}
              <div ref={buttonRef} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => {
              setIsHovering(false);
              setMousePosition({
                x: 0,
                y: 0
              });
            }} className="flex-1 relative">
                <Link to="https://betravaux.com/connexion-pro/" className="block">
                  <Button size="lg" variant="secondary" className="w-full shadow-soft transition-all duration-[var(--duration-medium)]" style={{
                  transform: isHovering ? `translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.15}px)` : "translate(0, 0)"
                }}>Me connecter<ArrowRight className="size-5 ml-2" /></Button>
                </Link>
              </div>
            </div>

            {}
            <div className="pt-12 border-t border-border/10 space-y-4">
              <p className="text-sm text-foreground">
                Une question ? Notre équipe est à votre écoute
              </p>
              <div className="flex flex-wrap gap-6 text-sm">
                <Link to="tel:+33123456789" className="text-primary-dark hover:text-primary-dark transition-colors focus-ring rounded">
                  +33 6 32 41 41 20
                </Link>
                <Link to="mailto:contact@betravaux.fr" className="text-primary-dark hover:text-primary-dark transition-colors focus-ring rounded">
                  contact@betravaux.com
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}